import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import moment, { Moment } from 'moment';
import { isEmpty } from 'underscore';
import {
  ADVANCED_ANALYTICS,
  IPlotVariable,
  IBestDataset,
} from '../../../../types/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { DT0Format } from '../../../../utils/StaticVariable';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { MarkerItem } from '../../../../types/home';

interface UseStatisticsParams {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  dateRange: [moment.Moment, moment.Moment];
  onDatasetChange: (item: string) => void;
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  statisticsImages: string;
  statisticsValuesLinks: string[];
  error: string;
  pointName: string;
  updating: boolean;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
}

export const useStatisticsParams = (params: UseStatisticsParams) => {
  const {
    coordinates,
    previewDatasetId,
    datasets,
    dateRange,
    onDatasetChange,
    enrichedMetadata,
    onUpdate,
    statisticsImages,
    pointName,
    userInput,
    updateUserInput,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
    error,
  } = params;

  const selfTriggered = useRef(false);

  const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([]);
  const [dataset, setDataset] = useState(userInput.Statistics.dataset);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.Statistics.variable
  );
  const [startDate, setStartDate] = useState(
    userInput.Statistics.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.Statistics.endDate || moment(dateRange[1])
  );

  const getData = useCallback(
    (
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.STATISTICS,
          DatasetId: previewDatasetId,
          OriginalDatasetId: previewDatasetId,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  );

  const onUpdateStatistics = useCallback(
    (dateRange?: any) => {
      if (enrichedMetadata && variableX) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === coordinates.join()
        );

        if (task) {
          getData(
            variableX,
            task.OrderId,
            dateRange
              ? dateRange[0].format(DT0Format)
              : startDate.format(DT0Format),
            dateRange
              ? dateRange[1].format(DT0Format)
              : endDate.format(DT0Format)
          );
        }
      }
    },
    [
      enrichedMetadata,
      dataset,
      startDate,
      endDate,
      variableX,
      coordinates,
      getData,
    ]
  );

  const doVariableChange = useCallback(
    (active: number) => {
      const item = listVariablesX.find((e) => e.Index === active);
      setVariableX(item!);
    },
    [listVariablesX]
  );

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const onSelectDataset = useCallback(
    (value: string) => {
      onDatasetChange(value);
    },
    [onDatasetChange]
  );

  const handleCheckboxChange = useCallback(() => {
    selfTriggered.current = true;
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
  }, [
    startDate,
    endDate,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  ]);

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      return isAfterEndDate
        ? isAfterEndDate
        : current.isBefore(dateRange[0], 'day');
    },
    [endDate, dateRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      return isBeforeStartDate
        ? isBeforeStartDate
        : current.isAfter(dateRange[1], 'day');
    },
    [startDate, dateRange]
  );

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [datasets]);

  useEffect(() => {
    updateUserInput({
      Statistics: {
        dataset: dataset,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [dataset, variableX, startDate, endDate, updateUserInput]);

  useEffect(() => {
    if (enrichedMetadata) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === previewDatasetId &&
          task.CoordinatesString === coordinates.join()
      );
      if (task && task.ItemsInfo) {
        const itemsList = task.ItemsInfo.filter(
          (item) =>
            !item.Name.toLowerCase().includes('direction') &&
            !item.Name.toLowerCase().includes('directional')
        );
        setListVariableX(itemsList);
        if (
          previewDatasetId &&
          !isEmpty(datasets) &&
          !isEmpty(coordinates) &&
          statisticsImages === '' &&
          error === ''
        ) {
          setDataset(previewDatasetId);
          const indexX = itemsList[0];
          setVariableX(indexX);
          let initialDateRange = dateRange;
          if (isForAllAnalytics) {
            initialDateRange = globalDateRange as [
              moment.Moment,
              moment.Moment
            ];
            setStartDate(initialDateRange[0]);
            setEndDate(initialDateRange[1]);
          } else {
            setStartDate(moment(initialDateRange[0]));
            setEndDate(moment(initialDateRange[1]));
          }
          selfTriggered.current = true;
          getData(
            indexX,
            task.OrderId,
            initialDateRange[0].format(DT0Format),
            initialDateRange[1].format(DT0Format)
          );
        }
      }
    }
  }, [
    previewDatasetId,
    datasets,
    enrichedMetadata,
    coordinates,
    statisticsImages,
    error,
    dateRange,
    isForAllAnalytics,
    globalDateRange,
  ]);

  useEffect(() => {
    if (isForAllAnalytics && statisticsImages !== '') {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateStatistics(globalDateRange);
        setStartDate(globalDateRange[0]);
        setEndDate(globalDateRange[1]);
      }
    }
  }, [isForAllAnalytics, statisticsImages, globalDateRange]);

  return {
    listVariablesX,
    dataset,
    variableX,
    startDate,
    endDate,
    categories,
    onUpdateStatistics,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  };
};
